import * as React from 'react';
import { Transition } from 'react-spring/renderprops';
import Bugsnag from '@bugsnag/js';
import { springConfig } from '@99designs/apps/spa/common/utils/spring/spring';
import { DesignerCard_UserFragment } from '@99designs/apps/spa/pages/create/components/DesignerCard.generated';
import { trackRemovedDesignerEvent } from '@99designs/apps/spa/pages/create/events';
import RemoveStructuredBriefDesigners from '@99designs/apps/spa/pages/create/mutations/RemoveStructuredBriefDesigners';
import { RemoveStructuredBriefDesignersMutationVariables } from '@99designs/apps/spa/pages/create/mutations/RemoveStructuredBriefDesigners.generated';
import { InteractiveAvatar } from '@99designs/designers';

interface Brief {
    id: string;
    designers: DesignerCard_UserFragment[];
}

interface Props {
    brief: Brief;
    disableInteraction?: boolean;
}

interface State {
    expanded: boolean;
}

class DesignerStack extends React.Component<Props, State> {
    state = {
        expanded: false,
    };

    componentDidUpdate(prevProps: Props) {
        if (prevProps.brief.designers.length > 0 && this.props.brief.designers.length === 0) {
            this.setState({ expanded: false });
        }
    }

    toggleExpanded = () => {
        this.setState({ expanded: !this.state.expanded });
    };

    removeDesigner = (
        designer: DesignerCard_UserFragment,
        removeDesigners: (
            variables: RemoveStructuredBriefDesignersMutationVariables
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ) => Promise<any>
    ) => {
        const { brief } = this.props;

        trackRemovedDesignerEvent({
            id: brief.id,
            designer_id: parseInt(designer.id),
        });

        removeDesigners({
            id: brief.id,
            designerIds: [parseInt(designer.id)],
        }).catch(Bugsnag.notify);
    };

    renderDesignerStackItems = (
        removeDesigners: (
            variables: RemoveStructuredBriefDesignersMutationVariables
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ) => Promise<any>
    ) => {
        const { brief, disableInteraction } = this.props;
        const { expanded } = this.state;

        return (
            <div className="designer-stack__items">
                <Transition
                    items={
                        this.state.expanded
                            ? brief.designers
                            : brief.designers.length > 0
                            ? [brief.designers[0]]
                            : []
                    }
                    keys={(designer) => designer.id}
                    from={{ height: 0, opacity: 0 }}
                    enter={{ height: 'auto', opacity: 1 }}
                    leave={{ height: 0, opacity: 0 }}
                    config={springConfig(250)}
                    trail={20}
                >
                    {(designer, state, index) => (props) => {
                        const firstItem = index === 0;
                        const leave = 'leave';

                        return (
                            <div
                                key={designer.id}
                                style={
                                    !firstItem || state === leave
                                        ? { ...props }
                                        : { cursor: 'pointer' }
                                }
                            >
                                <div className="designer-stack__items__avatar">
                                    <InteractiveAvatar
                                        userId={designer.id}
                                        onRemoveClick={
                                            this.state.expanded && !disableInteraction
                                                ? () =>
                                                      this.removeDesigner(designer, removeDesigners)
                                                : undefined
                                        }
                                        onClick={firstItem ? this.toggleExpanded : undefined}
                                        badgeNumber={
                                            firstItem && !expanded
                                                ? brief.designers.length
                                                : undefined
                                        }
                                    />
                                </div>
                            </div>
                        );
                    }}
                </Transition>
            </div>
        );
    };

    render() {
        const { expanded } = this.state;

        return (
            <RemoveStructuredBriefDesigners>
                {(removeDesigners) => (
                    <Transition
                        items={expanded}
                        from={{ opacity: 0 }}
                        enter={{ opacity: 1 }}
                        leave={{ opacity: 0 }}
                        config={springConfig(300)}
                    >
                        {(expanded) => (props) =>
                            (
                                <>
                                    {expanded && (
                                        <div
                                            className="designer-stack__overlay"
                                            style={{ ...props }}
                                            onClick={this.toggleExpanded}
                                            data-testid="designer-stack-overlay"
                                        />
                                    )}
                                    <div className="designer-stack" data-testid="designer-stack">
                                        {this.renderDesignerStackItems(removeDesigners)}
                                    </div>
                                </>
                            )}
                    </Transition>
                )}
            </RemoveStructuredBriefDesigners>
        );
    }
}

export default DesignerStack;
